import { BrandingStyle, DisabledPrompt } from "@bcm-dev/bcms-vite";
import React from "react";
import { SVGAsset } from "res";

import "./styles.css";
/**
 * @function TableView
 * @param {STRING} className - Allows us to set a custom className. 
 * @param {React.Component} children - Allows us to inject other components/content. 
 * @returns {React.Component}
 * @example
 * <TableView className="some-class">
 *  <TableColumnContainer/>
 * </TableView>
 */
// Table
export default function TableView(props) {
  const className = props.className ?? "";

  return (
    <table className={`${className} table`}>{props.children}</table>
  );
}

/**
 * @function TableColumnContainer
 * @param {React.Component} children - Allows us to inject other components/content. 
 * @returns {React.Component}
 * @example
 * <TableColumnContainer className="some-class">
 *  <TableColumnView/>
 * </TableColumnContainer>
 */
// Header row
export function TableColumnContainer(props) {
  const className = props.className ?? "";
  return (
    <thead className={`${className} table-column-container`}>
      <tr>{props.children}</tr>
    </thead>
  );
}

/**
 * @function TableColumnView 
 * @param {STRING} className - Allows us to set a custom className. 
 * @param {STRING} text - String to display the table column name. 
 * @returns {React.Component}
 * @example
 * <TableColumnView className="some-class" text="Header for table">
 *  <TableHeaderView/>
 * </TableColumnView>
 */
// Header cell that goes in TableColumnContainer
export function TableColumnView(props) {
  const text = props.text ?? "" ;
  const className = props.className ?? "";
  const flexClassNames = props.flexClassNames ?? "flex-container flex-horizontal";
  const icon = props.icon;
  const dataPoint = props.dataPoint ? { "data-point" : props.dataPoint } : {};
  const colSpan = props.colSpan ? { "colSpan" : props.colSpan } : {};

  return (
    <th className={`${className} table-column-view`} { ...colSpan }>
      <div className={ flexClassNames }>
        <p { ...dataPoint }>{ text }</p>
        { icon !== undefined && <DisabledPrompt><SVGAsset id={ icon } className="icon clickable" /></DisabledPrompt> }
      </div>
      
    </th>
  );
}


/**
 * @function TableHeaderView
 * @param {INT} colSpan - Allows us to set the colspan number, default is 2.
 * @param {STRING} tableHeader - String to show the table header text. 
 * @param {React.Component} children - Allows us to inject other components/content. 
 * @param {STRING} data-point - Allows us to set a data-point to the text
 * @returns {React.Component}
 * @example
 * <TableHeaderView colSpan="4" text="This is some grey-background text">
 *  <TableItemContainer/>
 * </TableHeaderView>
 */
// Grey background header
export function TableHeaderView(props) {
  const colSpan = props.colSpan ?? 1;
  const text = props.text ?? "" ;
  const dataPoint = props.dataPoint ?? "";
  
  return (
    <tr>
      <BrandingStyle style={{ "backgroundColor" : "[[acct_details_header_bg_abi22]]" }} className="table-header-view" element={
        <td colSpan={colSpan}>
          <BrandingStyle style={{ "color" : "[[acct_details_header_text_abi22]]"}}>
            <span data-point={dataPoint}>
              {text}
            </span>
          </BrandingStyle>
        </td>
      } />
    </tr>
    
    
    
  );
}

/**
 * @function TableBody
 * @param {React.Component} children - Allows us to inject other components/content. 
 * @returns {React.Component} 
 * @example
 * <TableBody>
 *  <TableItemContainer>
 *    <TableItemView/>
 *  </TableItemContainer>
 * </TableBody>
 */
export function TableBody(props) {
  return (
    <tbody>
      {props.children}
    </tbody>
  );
}

/**
 * @function TableItemContainer
 * @param {React.Component} children - Allows us to inject other components/content. 
 * @returns {React.Component}
 * @example
 * <TableItemContainer>
 *  <TableItemView/>
 * </TableItemContainer>
 */
// Table body rows
export function TableItemContainer(props) {
  return (
      <tr className="table-item-container">{props.children}</tr>
  );
}

/**
 * @function TableItemView
 * @param {STRING} className - Allows us to pass a specific className to the table cell.
 * @param {STRING} data-point - The data point that should be added 
 * @param {React.Component} children - Allows us to set the content of the table cell. 
 * @returns {React.Component}
 * @example
 * <TableItemView className="some-class">
 *  <span>[asset]</span>
 * </TableItemView>
 */
// Table cells that go in TableItemContainer that has anything but text
export function TableItemView(props) {
  const className = props.className ?? "";
  const dataPoint = setDataPoint(props);
  const content = dataPoint !== undefined 
    ? (<div data-point={dataPoint}>{props.children}</div>)
    : <div>{props.children}</div>;

  return (
    <td className={`${className} table-item-view`}>
      {content}
    </td>
  );
}

/**
 * @function TableItemTextView text
 * @param {STRING} text - Allows us to set the text data in the table in a table cell (td). 
 * @param {STRING} className - Allows us to set a custom className. 
 * @returns {React.Component}
 * @example
 * <TableItemTextView className="some-class" text="This text goes in a cell" />
 */
// Table cell that go in TableItemContainer that has just text in it
export function TableItemTextView(props) {
  const className = props.className ?? "";
  const text = props.text ?? "" ;
  const dataPoint = setDataPoint(props);
  const colSpan = props.colSpan ? { colSpan: props.colSpan } : {};
  const content = dataPoint !== undefined 
    ? (<span data-point={dataPoint}>{text}</span>)
    : <span>{text}</span>;

  return (
    <td className={`${className} table-item-text-view`} { ...colSpan }>
      {content}
    </td>
  );
}

//This is a temporary function that will set a data point for the given property value
//Until we can refactor components to use the same dataPoint property 
function setDataPoint(props){
  if(props.dataPoint !== undefined){
    return props.dataPoint;
  }else if(props["data-point"] !== undefined){
    return props["data-point"];
  }else{
    return undefined;
  }
}
